import { inject, Signal } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { EntityProps, EntityState, removeEntity, setAllEntities, updateEntity } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';

import { Compétence } from '../../croisement-référentiel/models/compétences';
import { SelectedEntityState } from '../../shared/store/selected-entity.feature';
import { CompétenceService } from '../compétences.service';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { HttpErrorResponse } from '@angular/common/http';

export function avecMéthodesCompétence<_>() {
  return signalStoreFeature(
    {
      state: type<EntityState<Compétence> & SelectedEntityState>(),
      props: type<EntityProps<Compétence> & { selectedEntity: Signal<Compétence | null> }>(),
    },
    withMethods((store) => ({
      modifierCompetence: (state: { competence: Compétence }) =>
        patchState(store, updateEntity({ id: state.competence.id, changes: state.competence })),
      chargerCompetences: (state: { competences: Compétence[] }) =>
        patchState(store, setAllEntities(state.competences)),
      supprimerCompetence: (id: string) => patchState(store, removeEntity(id)),
    })),
    withMethods((store, service = inject(CompétenceService)) => ({
      modifier: rxMethod<{ competence: Compétence; idReferentiel: string }>(
        pipe(
          tap(({ competence }) => store.modifierCompetence({ competence })),
          switchMap(({ competence, idReferentiel }) =>
            service
              .modifier(competence.id, { idsAptitudes: competence.aptitudes.map((a) => a.id) }, idReferentiel)
              .pipe(
                tapResponse({
                  next: () => {},
                  error: (err) => {
                    const toastrId = uuidv7();
                    toast.custom(ToastrErrorComponent, {
                      componentProps: {
                        message: `Un problème est survenu lors de la modification de la compétences, contactez le support.`,
                        id: toastrId,
                      },
                      id: toastrId,
                    });
                    console.error(err);
                  },
                }),
              ),
          ),
        ),
      ),
      parAptitude: rxMethod<{ idAptitude: string; idReferentiel: string; next: () => void }>(
        pipe(
          switchMap(({ idAptitude, idReferentiel, next }) =>
            service.getAll(idAptitude, idReferentiel).pipe(
              tapResponse({
                next: (result) => {
                  store.chargerCompetences({ competences: result.items });
                  next();
                },
                error: (err: HttpErrorResponse) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message:
                        err.error.detail ??
                        `Un problème est survenu lors de la récupération des compétences, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
      supprimer: rxMethod<{ idCompetence: string; next: () => void }>(
        pipe(
          switchMap(({ idCompetence, next }) =>
            service.supprimer(idCompetence).pipe(
              tapResponse({
                next: () => {
                  store.supprimerCompetence(idCompetence);
                  next();
                },
                error: (err: HttpErrorResponse) => {
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
    })),
  );
}
